import React, { useState } from "react";
import { Card, CardBody, Container, Row, Col, Input, Button } from "reactstrap";
import { useTranslation } from "../../hooks/useTranslation";
import { useFetchStorefrontProductsQuery } from "../../api/api";

const ProductsPage = () => {
  const t = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [brandFilter, setBrandFilter] = useState("");

  // Fetch all products
  const {
    data: products = [],
    isFetching: isLoadingProducts,
    isError: isProductsError,
  } = useFetchStorefrontProductsQuery();

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      !searchTerm ||
      (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.color && product.color.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesBrand =
      !brandFilter ||
      (product.brand && product.brand.toLowerCase().includes(brandFilter.toLowerCase()));

    return matchesSearch && matchesBrand;
  });

  // Get unique brands for filter
  const uniqueBrands = [...new Set(products.map((p) => p.brand).filter(Boolean))].sort();

  return (
    <div className="client-products-page">
      <Container fluid className="py-5">
        {/* Header Section */}
        <Row className="mb-5">
          <Col md="12" className="text-center">
            <h1 className="display-3 mb-3">
              {t("client.products") || "Our Products"}
            </h1>
            <p className="lead text-muted">
              {t("client.productsDescription") || 
                "Browse through our amazing collection of products."}
            </p>
          </Col>
        </Row>

        {/* Filters Section */}
        <Row className="mb-4">
          <Col md="6" className="mb-3">
            <Input
              type="text"
              placeholder={t("client.searchProducts") || "Search products by code, brand, or color..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control-lg"
            />
          </Col>
          <Col md="4" className="mb-3">
            <Input
              type="select"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="form-control-lg"
            >
              <option value="">{t("client.allBrands") || "All Brands"}</option>
              {uniqueBrands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </Input>
          </Col>
          <Col md="2" className="mb-3">
            <Button
              color="secondary"
              block
              onClick={() => {
                setSearchTerm("");
                setBrandFilter("");
              }}
              className="h-100"
            >
              {t("common.filter") || "Reset"}
            </Button>
          </Col>
        </Row>

        {/* Products Grid */}
        {isLoadingProducts ? (
          <Row>
            <Col md="12" className="text-center py-5">
              <p className="lead">{t("common.loading") || "Loading products..."}</p>
            </Col>
          </Row>
        ) : isProductsError ? (
          <Row>
            <Col md="12" className="text-center py-5">
              <p className="text-danger">{t("messages.failedToLoad") || "Failed to load products"}</p>
            </Col>
          </Row>
        ) : filteredProducts.length === 0 ? (
          <Row>
            <Col md="12" className="text-center py-5">
              <p className="lead text-muted">
                {t("client.noProductsFound") || "No products found. Try adjusting your filters."}
              </p>
            </Col>
          </Row>
        ) : (
          <>
            <Row className="mb-3">
              <Col md="12">
                <p className="text-muted">
                  {t("client.showingResults") || "Showing"} {filteredProducts.length}{" "}
                  {t("client.of") || "of"} {products.length} {t("client.products") || "products"}
                </p>
              </Col>
            </Row>
            <Row>
              {filteredProducts.map((product) => (
                <Col lg="3" md="4" sm="6" xs="12" key={product.id} className="mb-4">
                  <Card className="product-card h-100 border-0 shadow-sm hover-lift">
                    <CardBody className="p-4">
                      <div className="product-header mb-3">
                        <h5 className="product-brand mb-2 font-weight-bold">
                          {product.brand || "—"}
                        </h5>
                        <div className="product-code mb-2">
                          <span className="badge badge-primary badge-pill">
                            {product.code || "—"}
                          </span>
                        </div>
                      </div>
                      
                      <div className="product-details mb-3">
                        {product.color && (
                          <p className="mb-1">
                            <strong>{t("table.color") || "Color"}:</strong> {product.color}
                          </p>
                        )}
                        {product.number && (
                          <p className="mb-1">
                            <strong>{t("table.number") || "Size"}:</strong> {product.number}
                          </p>
                        )}
                        {product.specific_type && (
                          <p className="mb-1">
                            <strong>{t("table.type") || "Type"}:</strong> {product.specific_type}
                          </p>
                        )}
                        {product.location && (
                          <p className="mb-1 text-muted">
                            <small>
                              <i className="tim-icons icon-pin"></i> {product.location}
                            </small>
                          </p>
                        )}
                      </div>

                      {product.price && (
                        <div className="product-price mt-auto">
                          <div className="d-flex justify-content-between align-items-center">
                            <h4 className="text-primary mb-0 font-weight-bold">
                              €{Number(product.price).toFixed(2)}
                            </h4>
                            {product.quantity !== undefined && (
                              <span className="badge badge-info">
                                {t("table.quantity") || "Qty"}: {product.quantity}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default ProductsPage;
